import { useContext, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useCurrentPromo } from '@wix/da-shared-react/pkg/utils/hooks/useCurrentPromo';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import SaleBanner from '@wix/da-shared-react/pkg/banners/SaleBanner';
import DailyChallengeBanner from '../DailyChallengeBanner';
import { getDailyChallengeTag } from '../../../redux/dreamup/selectors';
import PromptCount from '@wix/da-dreamup/pkg/components/PromptCount';
import PromptForm from '@wix/da-dreamup/pkg/components/PromptForm';
import s from './Header.scss';

export function jumpToDreamupHeader() {
  if (window) {
    // I would use `window.location.hash` or `window.location.href` here, but that wasn't jumping to the top
    // for some reason. Maybe the body scroll lock was interfering?
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

interface Props {}

const Header: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const currentPromo = useCurrentPromo('self');

  const dailyChallengeTag = useSelector(getDailyChallengeTag);

  const renderSaleBanner = () => {
    if (!currentPromo) {
      return;
    }

    return (
      <SaleBanner
        className={s['sale-banner']}
        giftRecipient={undefined}
        discountValuePercent={currentPromo?.discountValuePercent}
        endDate={currentPromo?.endDate}
        referralType={ReferralType.PROMOTION_BANNER_DREAMUP}
      />
    );
  };

  return (
    <div className={s['root']}>
      {isMobile && renderSaleBanner()}

      {!isMobile && (
        <div className={s['count-and-banner']}>
          <PromptCount />
          {dailyChallengeTag && (
            <DailyChallengeBanner tag={dailyChallengeTag} className={s['banner']} />
          )}
        </div>
      )}

      <VisuallyHidden>
        <h1 className={s['heading']}>{t('dreamup.header.heading')}</h1>
      </VisuallyHidden>

      <PromptForm />
    </div>
  );
};

Header.displayName = 'Header';

export default Header;
